import { DotsVerticalIcon, TrashIcon } from "@heroicons/react/outline";
import { UserAvatar } from "@promptlayer/ui";
import { Fragment } from "react";

import {
  NormalizedGroupedInvite,
  NormalizedMemberData,
  isOrganizationMember,
} from "../hooks/utils";

import { Button } from "@/components/ui/button";
import { Action, DropdownMenu } from "@/components/ui/DropdownMenu";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { capitalize } from "@/utils/strings";
import { formatTimestamp } from "@/utils/timestamp";

interface UserInfoSectionProps {
  member: NormalizedMemberData;
  orgMember: NormalizedMemberData | null;
  groupedInvite: NormalizedGroupedInvite | null;
  isOrgOwner: boolean;
  isViewingSelf: boolean;
  userOwnerRoles: { id: number; name: string }[];
  userInfoActions: Action<undefined>[];
  deleteButtonLabel: string;
  handleRoleChange: (roleId: string) => void;
  handleDeleteWithConfirm: () => Promise<void>;
  isNarrowViewport: boolean;
}

export const UserInfoSection = ({
  member,
  orgMember,
  groupedInvite,
  isOrgOwner,
  isViewingSelf,
  userOwnerRoles,
  userInfoActions,
  deleteButtonLabel,
  handleRoleChange,
  handleDeleteWithConfirm,
  isNarrowViewport,
}: UserInfoSectionProps) => {
  const getEmail = () => {
    if (isOrganizationMember(member)) {
      return member.user.email;
    }
    if (groupedInvite) {
      return groupedInvite.invitee_email;
    }
    return "";
  };
  const email = getEmail();

  const getStatusText = () => {
    if (!groupedInvite) {
      return null;
    }
    const allExpired = groupedInvite.invites.every(
      i => i.is_expired || new Date(i.expires_at) < new Date(),
    );
    return allExpired ? "Expired" : "Pending";
  };
  const statusText = getStatusText();

  return (
    <section>
      <div className="rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-3">
        <div className="flex items-center gap-2.5">
          <UserAvatar email={email} className="h-8 w-8 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">{email || "-"}</p>
            <p className="flex flex-wrap items-center gap-x-1 gap-y-1.5 text-xs text-gray-500">
              {orgMember &&
                "user_id" in orgMember &&
                [
                  {
                    key: "role",
                    content: capitalize(orgMember.organization_role.name),
                  },
                  ...(orgMember.user.last_active
                    ? [
                        {
                          key: "last-active",
                          content: `Last active ${formatTimestamp(orgMember.user.last_active)}`,
                        },
                      ]
                    : []),
                ].map((item, index, arr) => (
                  <Fragment key={item.key}>
                    <span>{item.content}</span>
                    {index !== arr.length - 1 && (
                      <div className="h-3 w-px bg-gray-300" aria-hidden />
                    )}
                  </Fragment>
                ))}
              {statusText}
            </p>
          </div>
          {(orgMember || groupedInvite) && (
            <div className="flex shrink-0 items-center justify-end gap-2">
              {isNarrowViewport ? (
                <DropdownMenu<undefined>
                  menuItems={userInfoActions}
                  icon={<DotsVerticalIcon className="h-5 w-5 text-gray-500" />}
                  triggerClassName="!bg-transparent border-0 p-1"
                />
              ) : (
                <>
                  {orgMember &&
                    "user_id" in orgMember &&
                    isOrgOwner &&
                    userOwnerRoles.map((role: { id: number; name: string }) => {
                      const isCurrentRole = orgMember.organization_role.id === role.id;
                      return (
                        <Button
                          key={role.id}
                          variant="outline"
                          size="sm"
                          disabled={isCurrentRole}
                          onClick={() => handleRoleChange(role.id.toString())}
                        >
                          Change to {capitalize(role.name)}
                        </Button>
                      );
                    })}
                  <BasicTooltip
                    content={
                      !isOrgOwner && !isViewingSelf
                        ? "Only organization owners can remove members"
                        : deleteButtonLabel
                    }
                  >
                    <span className="flex">
                      <Button
                        variant="destructiveLink"
                        size="tinyIcon"
                        onClick={handleDeleteWithConfirm}
                        disabled={!isOrgOwner && !isViewingSelf}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </span>
                  </BasicTooltip>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
